import { useEffect, useRef, useState } from "react";
import { getAdminReportsOverview } from "../api/instateamReportsAdminApi";
import { AppIcon, StatusBanner } from "../components/InstateamMasterComponents";
import { currencyFormatter } from "../utils/adminHelpers";

const RANGE_OPTIONS = ["Today", "Week", "Month", "Year", "Custom"];
const COLORS = ["#2f7df6", "#22c55e", "#f4b400", "#8b5cf6", "#9ca3af", "#ef4444"];

function getMonthKey(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function formatMinutes(minutes) {
  const safe = Math.max(0, Math.round(Number(minutes || 0)));
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  return `${hours}h ${String(mins).padStart(2, "0")}m`;
}

function shortDate(value) {
  const date = value ? new Date(`${value}T00:00:00`) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short" }).format(date);
}

function csvDate(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return `'${value}`;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : `'${date.toISOString().slice(0, 10)}`;
}

function downloadCsv(fileName, rows) {
  const csv = rows
    .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

function StatCard({ stat }) {
  return (
    <article className="admin-report-stat-card">
      <div className={`admin-report-stat-icon ${stat.tone}`}>
        <AppIcon name={stat.icon} />
      </div>
      <div>
        <span>{stat.label}</span>
        <strong>{stat.value}</strong>
        <small className={stat.positive ? "positive" : stat.negative ? "negative" : ""}>{stat.helper}</small>
      </div>
    </article>
  );
}

function Sparkline({ values = [], color = "#2f7df6" }) {
  const max = Math.max(...values, 1);
  const path = values
    .map((value, index) => {
      const x = (index / Math.max(values.length - 1, 1)) * 100;
      const y = 30 - (Number(value || 0) / max) * 22;
      return `${index ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <svg className="admin-report-sparkline" viewBox="0 0 100 34" aria-hidden="true">
      <path d={path} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function MiniCard({ icon, tone, label, value, helper, color, values }) {
  return (
    <article className="admin-report-mini-card">
      <div className={`admin-report-stat-icon ${tone}`}>
        <AppIcon name={icon} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{helper}</small>
      </div>
      <Sparkline values={values} color={color} />
    </article>
  );
}

function AttendanceChart({ series }) {
  const chartSeries = series.length ? series : [{ label: "No data", present: 0, absent: 0, onLeave: 0, halfDay: 0 }];
  const max = Math.max(...chartSeries.flatMap((item) => [item.present, item.absent, item.onLeave, item.halfDay]), 1);
  const keys = [
    ["present", "#22c55e"],
    ["absent", "#ef4444"],
    ["onLeave", "#f4b400"],
    ["halfDay", "#2f7df6"],
  ];

  const buildPath = (key) =>
    chartSeries
      .map((item, index) => {
        const x = 44 + index * (500 / Math.max(chartSeries.length - 1, 1));
        const y = 220 - (Number(item[key] || 0) / max) * 180;
        return `${index ? "L" : "M"}${x} ${y}`;
      })
      .join(" ");

  return (
    <svg className="admin-report-attendance-chart" viewBox="0 0 590 250">
      {[0, 25, 50, 75, 100].map((tick) => (
        <g key={tick}>
          <line x1="40" y1={220 - tick * 1.8} x2="560" y2={220 - tick * 1.8} />
          <text x="8" y={224 - tick * 1.8}>{tick}</text>
        </g>
      ))}
      {keys.map(([key, color]) => <path key={key} d={buildPath(key)} stroke={color} />)}
      {chartSeries.map((item, index) => (
        <text key={item.dateKey || index} className="axis-label" x={34 + index * (500 / Math.max(chartSeries.length - 1, 1))} y="242">
          {item.label}
        </text>
      ))}
    </svg>
  );
}

function Donut({ title, total, items }) {
  const safeItems = items.length ? items : [{ label: "No data", count: 1 }];
  const totalCount = safeItems.reduce((sum, item) => sum + Number(item.count || 0), 0) || 1;
  let offset = 0;

  return (
    <article className="admin-report-card">
      <h3>{title}</h3>
      <div className="admin-report-donut-layout">
        <div className="admin-report-donut">
          <svg viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="43" className="base" />
            {safeItems.map((item, index) => {
              const value = (Number(item.count || 0) / totalCount) * 100;
              const segment = (
                <circle
                  key={item.label}
                  cx="60"
                  cy="60"
                  r="43"
                  pathLength="100"
                  style={{
                    stroke: COLORS[index % COLORS.length],
                    strokeDasharray: `${value} ${100 - value}`,
                    strokeDashoffset: -offset,
                  }}
                />
              );
              offset += value;
              return segment;
            })}
          </svg>
          <div><strong>{total}</strong><span>Total</span></div>
        </div>
        <div className="admin-report-legend">
          {safeItems.slice(0, 6).map((item, index) => (
            <span key={item.label}>
              <i style={{ background: COLORS[index % COLORS.length] }} />
              {item.label}
              <small>{item.count} ({Math.round((Number(item.count || 0) / totalCount) * 100)}%)</small>
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}

export default function ReportsPage({ token, searchText = "", onSearchChange, onNotify, onOpenEmployeePerformance }) {
  const [range, setRange] = useState("Week");
  const [month, setMonth] = useState(getMonthKey);
  const [department, setDepartment] = useState("all");
  const [designation, setDesignation] = useState("all");
  const [status, setStatus] = useState("all");
  const search = searchText;
  const setSearch = onSearchChange || (() => {});
  const [overview, setOverview] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const monthRef = useRef(null);

  async function fetchReports() {
    if (!token) return;
    try {
      setLoading(true);
      const payload = await getAdminReportsOverview(token, {
        range: range.toLowerCase(),
        month,
        department,
        designation,
        status,
        search,
      });
      setOverview(payload.overview || null);
    } catch (error) {
      setMessage({ type: "error", text: error.message });
      onNotify?.({ type: "error", text: error.message });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, range, month, department, designation, status, search]);

  const metrics = overview?.metrics || {};
  const series = overview?.series || [];
  const stats = [
    { label: "Total Employees", value: metrics.totalEmployees || 0, helper: "live employee count", icon: "employees", tone: "blue", positive: true },
    { label: "Active Employees", value: metrics.activeEmployees || 0, helper: "active right now", icon: "activeUsers", tone: "yellow", positive: true },
    { label: "Attendance %", value: `${metrics.attendancePercent || 0}%`, helper: "selected range", icon: "attendance", tone: "green", positive: true },
    { label: "Total Payroll", value: currencyFormatter.format(metrics.totalPayroll || 0), helper: "processed payroll", icon: "payroll", tone: "purple", positive: true },
    { label: "Leaves Taken", value: metrics.leavesTaken || 0, helper: "approved leave days", icon: "leaves", tone: "red", negative: true },
    { label: "Tasks Completed", value: metrics.tasksCompleted || 0, helper: "completed tasks", icon: "tasks", tone: "blue", positive: true },
  ];

  function getReportHeader(title) {
    return [
      [title],
      ["Range", `${overview?.startKey || ""} to ${overview?.endKey || ""}`],
      ["Selected Month", overview?.monthKey || month],
      ["Department Filter", department],
      ["Designation Filter", designation],
      ["Status Filter", status],
      ["Search", search],
      [],
    ];
  }

  function getDepartmentRows(selectedDepartment = "") {
    const rows = overview?.departmentRows || [];
    return selectedDepartment
      ? rows.filter((row) => row.department === selectedDepartment)
      : rows;
  }

  function getDetailRows(key, selectedDepartment = "") {
    const rows = overview?.details?.[key] || [];
    return selectedDepartment
      ? rows.filter((row) => row.department === selectedDepartment)
      : rows;
  }

  function buildSummaryReportRows() {
    return [
      ...getReportHeader("Admin Reports Summary"),
      ["Metric", "Value"],
      ...stats.map((stat) => [stat.label, stat.value]),
      [],
      ["Department", "Employees", "Attendance %", "Leaves Taken", "Tasks Completed", "Time Logged", "Payroll"],
      ...getDepartmentRows().map((row) => [
        row.department,
        row.employees,
        row.attendancePercent,
        row.leavesTaken,
        row.tasksCompleted,
        formatMinutes(row.timeLoggedMinutes),
        row.payroll,
      ]),
    ];
  }

  function buildDepartmentReportRows(selectedDepartment = "") {
    return [
      ...getReportHeader(selectedDepartment ? `${selectedDepartment} Department Report` : "Department Report"),
      ["Department", "Employees", "Attendance %", "Leaves Taken", "Tasks Completed", "Time Logged", "Payroll"],
      ...getDepartmentRows(selectedDepartment).map((row) => [
        row.department,
        row.employees,
        row.attendancePercent,
        row.leavesTaken,
        row.tasksCompleted,
        formatMinutes(row.timeLoggedMinutes),
        row.payroll,
      ]),
    ];
  }

  function buildEmployeePerformanceRows() {
    return [
      ...getReportHeader("Employee Performance Report"),
      ["Employee ID", "Employee Name", "Department", "Designation", "Score", "Score Percent"],
      ...(overview?.employeePerformance || []).map((employee) => [
        employee.employeeId,
        employee.fullName,
        employee.department,
        employee.designation,
        employee.score,
        `${employee.scorePercent}%`,
      ]),
    ];
  }

  function buildAttendanceRows() {
    return [
      ...getReportHeader("Attendance Report"),
      ["Employee ID", "Employee Name", "Department", "Designation", "Date", "Status", "Check In", "Check Out", "Work Time", "Location"],
      ...getDetailRows("attendanceRecords").map((record) => [
        record.employeeId,
        record.fullName,
        record.department,
        record.designation,
        csvDate(record.dateKey),
        record.status,
        record.checkInTime,
        record.checkOutTime,
        formatMinutes(record.workMinutes),
        record.location,
      ]),
    ];
  }

  function buildLeaveRows() {
    return [
      ...getReportHeader("Leave Report"),
      ["Employee ID", "Employee Name", "Department", "Designation", "Leave Type", "Status", "From", "To", "Total Days", "Reason"],
      ...getDetailRows("leaves").map((leave) => [
        leave.employeeId,
        leave.fullName,
        leave.department,
        leave.designation,
        leave.leaveType,
        leave.status,
        leave.fromDateKey,
        leave.toDateKey,
        leave.totalDays,
        leave.reason,
      ]),
    ];
  }

  function buildPayrollRows() {
    return [
      ...getReportHeader("Payroll Summary Report"),
      ["Employee ID", "Employee Name", "Department", "Designation", "Month", "Status", "Gross Salary", "Deductions", "Net Salary", "CTC Annual"],
      ...getDetailRows("payrollRecords").map((record) => [
        record.employeeId,
        record.fullName,
        record.department,
        record.designation,
        record.monthKey,
        record.status,
        record.grossSalary,
        record.totalDeductions,
        record.netSalary,
        record.ctcAnnual,
      ]),
    ];
  }

  function buildTaskRows() {
    const employeesById = new Map(
      (overview?.details?.employees || []).map((employee) => [String(employee.employeeObjectId), employee])
    );

    return [
      ...getReportHeader("Task Completion Report"),
      ["Employee ID", "Employee Name", "Department", "Designation", "Task", "Status", "Priority", "Due Date", "Completed At"],
      ...getDetailRows("tasks").map((task) => {
        const employee = employeesById.get(String(task.employee)) || {};
        return [
          employee.employeeId || "",
          employee.fullName || "",
          employee.department || "",
          employee.designation || "",
          task.title,
          task.status,
          task.priority,
          csvDate(task.dueDate),
          csvDate(task.completedAt),
        ];
      }),
    ];
  }

  function buildDocumentRows() {
    return [
      ...getReportHeader("Document Upload Report"),
      ["Employee ID", "Employee Name", "Department", "Designation", "Document", "Status", "Uploaded At"],
      ...getDetailRows("documentRequests").map((request) => [
        request.employeeId,
        request.fullName,
        request.department,
        request.designation,
        request.documentLabel || request.documentKey,
        request.status,
        csvDate(request.uploadedAt),
      ]),
    ];
  }

  function buildComplaintRows() {
    return [
      ...getReportHeader("Complaint Report"),
      ["Employee ID", "Employee Name", "Department", "Designation", "Category", "Subject", "Status", "Created At"],
      ...getDetailRows("complaints").map((complaint) => [
        complaint.employeeId,
        complaint.fullName,
        complaint.department,
        complaint.designation,
        complaint.category,
        complaint.subject,
        complaint.status,
        csvDate(complaint.createdAt),
      ]),
    ];
  }

  function buildAllReportRows() {
    return [
      ...buildSummaryReportRows(),
      [],
      ...buildEmployeePerformanceRows(),
      [],
      ...buildAttendanceRows(),
      [],
      ...buildLeaveRows(),
      [],
      ...buildPayrollRows(),
      [],
      ...buildTaskRows(),
      [],
      ...buildDocumentRows(),
      [],
      ...buildComplaintRows(),
    ];
  }

  function getReportRows(name = "admin-reports.csv", selectedDepartment = "") {
    const normalizedName = String(name || "").toLowerCase().replace(/\s+/g, "-");
    if (selectedDepartment) return buildDepartmentReportRows(selectedDepartment);
    if (normalizedName.includes("employee-performance")) return buildEmployeePerformanceRows();
    if (normalizedName.includes("attendance")) return buildAttendanceRows();
    if (normalizedName.includes("leave")) return buildLeaveRows();
    if (normalizedName.includes("payroll")) return buildPayrollRows();
    if (normalizedName.includes("task")) return buildTaskRows();
    if (normalizedName.includes("document")) return buildDocumentRows();
    if (normalizedName.includes("complaint")) return buildComplaintRows();
    if (normalizedName.includes("all-admin")) return buildAllReportRows();
    return buildSummaryReportRows();
  }

  function exportReport(name = "admin-reports.csv", options = {}) {
    const rows = getReportRows(name, options.department);
    downloadCsv(name, rows);
    setMessage({ type: "success", text: "Report downloaded successfully." });
  }

  function clearFilters() {
    setDepartment("all");
    setDesignation("all");
    setStatus("all");
    setSearch("");
    setRange("Week");
    setMonth(getMonthKey());
  }

  return (
    <section className="admin-reports-page">
      {message ? <StatusBanner type={message.type} text={message.text} onClose={() => setMessage(null)} /> : null}

      <div className="admin-report-toolbar">
        <button
          type="button"
          className="admin-report-date"
          onClick={() => {
            if (monthRef.current?.showPicker) {
              monthRef.current.showPicker();
              return;
            }
            monthRef.current?.focus();
            monthRef.current?.click();
          }}
        >
          <AppIcon name="calendar" />
          {overview?.startKey ? `${shortDate(overview.startKey)} - ${shortDate(overview.endKey)}` : "Select month"}
          <input ref={monthRef} type="month" value={month} onChange={(event) => setMonth(event.target.value || getMonthKey())} />
          <AppIcon name="chevronDown" />
        </button>
        <select value={department} onChange={(event) => setDepartment(event.target.value)}>
          <option value="all">All Departments</option>
          {(overview?.filters?.departments || []).map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
        <select value={designation} onChange={(event) => setDesignation(event.target.value)}>
          <option value="all">All Designations</option>
          {(overview?.filters?.designations || []).map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <label className="admin-report-search"><AppIcon name="search" /><input value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => event.key === "Enter" && fetchReports()} placeholder="Search employee by name, ID..." /></label>
        <button type="button" onClick={fetchReports}><AppIcon name="refresh" /></button>
        <button type="button" onClick={clearFilters}>Clear Filters</button>
      </div>

      <div className="admin-report-top-actions">
        <div className="admin-report-range-tabs">
          {RANGE_OPTIONS.map((item) => <button key={item} type="button" className={range === item ? "active" : ""} onClick={() => setRange(item)}>{item}</button>)}
        </div>
        <button type="button" className="admin-report-export" onClick={() => exportReport()}><AppIcon name="download" /> Export Report <AppIcon name="chevronDown" /></button>
      </div>

      <div className="admin-report-stat-grid">
        {stats.map((stat) => <StatCard key={stat.label} stat={stat} />)}
      </div>

      <div className="admin-report-main-grid">
        <article className="admin-report-card admin-report-attendance-card">
          <div className="admin-report-card-head"><h3>Attendance Overview</h3><span>Daily</span></div>
          <div className="admin-report-chart-legend"><i className="present" />Present <i className="absent" />Absent <i className="leave" />On Leave <i className="half" />Half Day</div>
          <AttendanceChart series={series} />
        </article>
        <Donut title="Employees by Department" total={metrics.totalEmployees || 0} items={overview?.departmentDistribution || []} />
        <Donut title="Leaves Overview" total={metrics.leavesTaken || 0} items={overview?.leaveDistribution || []} />
      </div>

      <div className="admin-report-mini-grid">
        <MiniCard icon="clock" tone="yellow" label="Time Logged (This Range)" value={formatMinutes(metrics.timeLoggedMinutes)} helper="from attendance" color="#2f7df6" values={series.map((item) => item.present)} />
        <MiniCard icon="chat" tone="purple" label="Complaints Raised" value={metrics.complaintsRaised || 0} helper={`${metrics.complaintsResolved || 0} resolved`} color="#8b5cf6" values={series.map((item) => item.complaintsRaised || 0)} />
        <MiniCard icon="employees" tone="yellow" label="Meetings Conducted" value={metrics.meetingsConducted || 0} helper="from task titles" color="#f4b400" values={series.map((item) => item.meetingsConducted || 0)} />
        <MiniCard icon="documents" tone="blue" label="Documents Uploaded" value={metrics.documentsUploaded || 0} helper="uploaded files" color="#2f7df6" values={series.map((item) => item.documentsUploaded || 0)} />
        <MiniCard icon="announcements" tone="green" label="Announcements Sent" value={metrics.announcementsSent || 0} helper="created in range" color="#22c55e" values={series.map((item) => item.announcementsSent || 0)} />
      </div>

      <div className="admin-report-content-grid">
        <article className="admin-report-card admin-report-table-card">
          <h3>Department Wise Summary</h3>
          <div className="admin-report-table">
            <div className="admin-report-table-head">
              <span>Department</span><span>Employees</span><span>Attendance %</span><span>Leaves Taken</span><span>Tasks Completed</span><span>Time Logged</span><span>Payroll</span><span>Actions</span>
            </div>
            {(overview?.departmentRows || []).map((row) => (
              <div key={row.department} className="admin-report-table-row">
                <strong>{row.department}</strong><span>{row.employees}</span><span>{row.attendancePercent}%</span><span>{row.leavesTaken}</span><span>{row.tasksCompleted}</span><span>{formatMinutes(row.timeLoggedMinutes)}</span><span>{currencyFormatter.format(row.payroll || 0)}</span>
                <span className="admin-report-row-actions"><button type="button" onClick={() => setMessage({ type: "success", text: `${row.department} report opened.` })}><AppIcon name="eye" /></button><button type="button" onClick={() => exportReport(`${row.department}-report.csv`, { department: row.department })}><AppIcon name="download" /></button></span>
              </div>
            ))}
          </div>
        </article>

        <aside className="admin-report-side">
          <article className="admin-report-card">
            <div className="admin-report-card-head"><h3>Employee Performance</h3><button type="button" onClick={() => exportReport("employee-performance.csv")}>View All</button></div>
            <div className="admin-report-employee-list">
              {(overview?.employeePerformance || []).map((employee) => (
                <button
                  type="button"
                  key={employee.employeeObjectId || employee.employeeId}
                  onClick={() => onOpenEmployeePerformance?.(employee, { range, month })}
                >
                  <span className="admin-report-avatar">{employee.fullName?.[0] || "E"}</span>
                  <div><strong>{employee.fullName}</strong><small>{employee.designation || employee.department}</small></div>
                  <em>{employee.score} / 10</em>
                </button>
              ))}
            </div>
            <button className="admin-report-full-button" type="button" onClick={() => exportReport("employee-performance.csv")}><AppIcon name="download" /> Download Employee Performance</button>
          </article>

          <article className="admin-report-card">
            <div className="admin-report-card-head"><h3>Recent Reports</h3><button type="button" onClick={() => exportReport("all-admin-reports.csv")}>View All</button></div>
            <div className="admin-report-recent-list">
              {(overview?.recentReports || []).map((report) => (
                <button type="button" key={report.title} onClick={() => exportReport(`${report.title}.csv`)}>
                  <AppIcon name="documents" />
                  <span>{report.title}<small>{report.type} - {report.size}</small></span>
                </button>
              ))}
            </div>
          </article>
        </aside>
      </div>

      {loading ? <div className="admin-report-loading">Refreshing reports...</div> : null}
    </section>
  );
}
